import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { EatingWayAreaImage, EatingWayListResponse } from 'features/EatingWay/models';
import { SaleType } from 'features/SaleType/models';
import { EatingWayTimeFrame, TimeFrame, UpdateEatingWayTimeFramePayload } from 'features/SaleType/models/TimeFrame';
import timeframeService from 'features/SaleType/timeframeService';
import { getAreaTypeByPatternAndPosition } from 'features/SaleType/utils';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  DISPLAY_PATTERN,
  EATING_WAY_AREA_IMAGE_TYPE_MAP,
  LANGUAGES,
  LANGUAGE_CODES,
  NUM_AREA_TYPE,
  PATTERN_TYPE,
  displayError,
  handleServiceError,
} from 'utils';
import SelectEatingWayModal, { SelectEatingWayModalProps } from '../SelectEatingWayModal';
import DefaultFlagButton from './DefaultFlagButton';
import './style.scss';

export interface EatingWayTimeFrameModalProps {
  timeFrame: TimeFrame;
  saleType: SaleType;
  onClose?: () => void;
}

function EatingWayTimeFrameModal({ timeFrame, saleType, onClose }: EatingWayTimeFrameModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // ref
  const numAreaElemRef = useRef<HTMLSelectElement>(null);
  const patternElemRef = useRef<HTMLSelectElement>(null);
  const languageElemRef = useRef<HTMLSelectElement>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [eatingWayTimeFrames, setEatingWayTimeFrames] = useState<EatingWayTimeFrame[]>();
  const [displayPattern, setDisplayPattern] = useState<{ [key: string]: string }>(
    DISPLAY_PATTERN[timeFrame.num_area || NUM_AREA_TYPE.TYPE1]
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>(LANGUAGE_CODES.JA);
  const [selectedNumArea, setSelectedNumArea] = useState<number>(timeFrame.num_area || NUM_AREA_TYPE.TYPE1);
  const [selectedPattern, setSelectedPattern] = useState<string>(
    timeFrame.pattern && timeFrame.pattern.length > 0 ? timeFrame.pattern : PATTERN_TYPE.P1
  );

  const [selectEatingWayModalData, setSelectEatingWayModalData] = useState<SelectEatingWayModalProps>();
  const [showSelectEatingWayModal, setShowEatingWayModal] = useState(false);
  const [defaultFlag, setDefaultFlag] = useState<number>(1);

  function loadTimeFrameData() {
    (async () => {
      try {
        const response = await timeframeService.getAll(Number(timeFrame.id));
        const timeframeData = response.data;
        const frames = timeframeData.eating_way_time_frames;
        setSelectedNumArea(timeframeData.num_area || NUM_AREA_TYPE.TYPE1);
        setSelectedPattern(
          timeframeData.pattern && timeframeData.pattern.length > 0 ? timeframeData.pattern : PATTERN_TYPE.P1
        );
        setDisplayPattern(DISPLAY_PATTERN[timeframeData.num_area || NUM_AREA_TYPE.TYPE1]);
        setEatingWayTimeFrames(timeframeData.eating_way_time_frames || []);

        if (!frames) {
          setDefaultFlag(0);
        } else if (frames.length === 0) {
          setDefaultFlag(1);
        } else {
          const defaultItem = frames.find((item) => item.default_flg === 1);

          if (defaultItem) {
            setDefaultFlag(defaultItem.position);
          } else {
            const allDefaultFlgZero = frames.every((item) => item.default_flg === 0);
            setDefaultFlag(allDefaultFlgZero ? 0 : 1);
          }
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  useEffect(() => {
    loadTimeFrameData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose() {
    onClose?.();
  }

  function handleChangeLanguage() {
    if (languageElemRef) {
      setSelectedLanguage(languageElemRef.current?.value || LANGUAGE_CODES.JA);
    }
  }

  function handleNumAreaChanged(e: React.ChangeEvent<HTMLSelectElement>) {
    const numArea = Number(e.target.value);
    setDisplayPattern(DISPLAY_PATTERN[numArea]);
  }

  function handleSwitchLayout() {
    if (patternElemRef && patternElemRef.current && numAreaElemRef && numAreaElemRef.current) {
      if (patternElemRef.current.value !== selectedPattern) {
        setSelectedPattern(patternElemRef.current?.value || PATTERN_TYPE.P1);
        setSelectedNumArea(Number(numAreaElemRef.current?.value) || NUM_AREA_TYPE['TYPE1']);
        setEatingWayTimeFrames([]);
        setDefaultFlag(1);
      }
    }
  }

  function handleSubmit() {
    // compose data
    const payload: UpdateEatingWayTimeFramePayload = {
      num_area: selectedNumArea,
      pattern: selectedPattern,
      sale_type_id: Number(saleType.id),
      eating_way_time_frames:
        eatingWayTimeFrames?.map((item) => ({
          eating_way_id: item.eating_way_id,
          time_frame_id: item.time_frame_id,
          position: item.position,
          area_type: item.area_type,
          default_flg: item.position === defaultFlag ? 1 : 0,
        })) || [],
    };

    (async () => {
      try {
        const response = await timeframeService.update(Number(timeFrame.id), payload);
        showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} onClose={handleClose} />);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleOpenSelectEatingWayModal(position: number, areaType: number, eatingWayIds: number[]) {
    setSelectEatingWayModalData({
      saleTypeId: Number(saleType.id),
      areaType,
      position,
      timeFrameId: Number(timeFrame.id),
      exceptEatingWayIds: eatingWayIds,
    });
    setShowEatingWayModal(true);
  }

  function handleCloseSelectEatingWayModal() {
    setSelectEatingWayModalData(undefined);
    setShowEatingWayModal(false);
  }

  function handleReflectSelectEatingWayModal(returnData: EatingWayListResponse, position: number) {
    const cloneEatingWayTimeFrames = [...(eatingWayTimeFrames || [])];
    const foundIndex = cloneEatingWayTimeFrames.findIndex((item) => item.position === position);
    if (foundIndex >= 0) {
      //update EatingWayTimeFrame by position
      cloneEatingWayTimeFrames[foundIndex].eating_way_id = returnData.id;
      cloneEatingWayTimeFrames[foundIndex].eating_way = returnData;
    } else {
      // compose newEatingWayTimeFrame
      const newEatingWayTimeFrame: EatingWayTimeFrame = {
        position,
        eating_way_id: returnData.id,
        time_frame_id: Number(timeFrame.id),
        eating_way: returnData,
        area_type: getAreaTypeByPatternAndPosition(selectedPattern, position),
        default_flg: defaultFlag,
      };
      cloneEatingWayTimeFrames.push(newEatingWayTimeFrame);
    }
    setEatingWayTimeFrames(cloneEatingWayTimeFrames);
  }

  function getDefaultImageByLanguageCode(areaImage: Partial<EatingWayAreaImage>, key: string) {
    const image = areaImage[key as keyof EatingWayAreaImage] as string | null;
    return image === null || image.length === 0 ? areaImage.image_en : image;
  }

  function getImageByLanguageAndAreaType(eatingWayTimeFrame: EatingWayTimeFrame | undefined, areaType: number) {
    if (!eatingWayTimeFrame) return null;

    const eatingWayAreaImage = eatingWayTimeFrame.eating_way.eating_way_area_images?.find(
      (item) => item.area_type === areaType
    );
    if (!eatingWayAreaImage) return null;

    switch (selectedLanguage) {
      case LANGUAGE_CODES.JA: {
        return eatingWayAreaImage.image_ja;
      }
      case LANGUAGE_CODES.EN: {
        return eatingWayAreaImage.image_en;
      }
      case LANGUAGE_CODES.ZH_HANT: {
        // return eatingWayAreaImage.image_zh_hant;
        return getDefaultImageByLanguageCode(eatingWayAreaImage, 'image_zh_hant');
      }
      case LANGUAGE_CODES.ZH_HANS: {
        // return eatingWayAreaImage.image_zh_hans;
        return getDefaultImageByLanguageCode(eatingWayAreaImage, 'image_zh_hans');
      }
      case LANGUAGE_CODES.KO: {
        // return eatingWayAreaImage.image_ko;
        return getDefaultImageByLanguageCode(eatingWayAreaImage, 'image_ko');
      }
      case LANGUAGE_CODES.VI: {
        // return eatingWayAreaImage.image_vi;
        return getDefaultImageByLanguageCode(eatingWayAreaImage, 'image_vi');
      }
      default: {
        return null;
      }
    }
  }

  function renderImageContent(img: string | null | undefined, areaType: number) {
    return img ? (
      <img src={process.env.REACT_APP_IMAGE_URL + img} alt="" />
    ) : (
      <div className="text-center">
        <div>{t('EatingWayTimeFrameModal.image')}</div>
        <div className="small">({EATING_WAY_AREA_IMAGE_TYPE_MAP[areaType]})</div>
      </div>
    );
  }

  function handleReflectDefaultFlagButton(default_flg: number) {
    setDefaultFlag(default_flg);
  }
  //【パターン１（p1）】
  function renderPatternP1() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P1, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className="start-mode-item violet w100 h100"
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          data-eating_way_id={eatingWayTimeFrame1?.eating_way_id}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン２A（p2a）】
  function renderPatternP2A() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P2A, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P2A, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item blue w50 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          data-eating_way_id={eatingWayTimeFrame1?.eating_way_id}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item blue w50 h100 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          data-eating_way_id={eatingWayTimeFrame2?.eating_way_id}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン２B（p2b）】
  function renderPatternP2B() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P2B, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P2B, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item red w66 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}

          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item green w33 h100 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン３A（p3a）】
  function renderPatternP3A() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3A, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3A, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3A, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item blue w50 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div className="d-flex flex-column w50 h100">
          <div
            className={`start-mode-item orange w100 h50 ${img2 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
            style={{ position: 'relative' }}
          >
            {imageContent2}
            {img2 && (
              <DefaultFlagButton
                is_default={defaultFlag === 2}
                onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
              />
            )}
          </div>
          <div
            className={`start-mode-item orange w100 h50 ${img3 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
            style={{ position: 'relative' }}
          >
            {imageContent3}
            {img3 && (
              <DefaultFlagButton
                is_default={defaultFlag === 3}
                onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  //【パターン３B（p3b）】
  function renderPatternP3B() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3B, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3B, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P3B, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item green w33 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item green w33 h100 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
        <div
          className={`start-mode-item green w33 h100 ${img3 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
          style={{ position: 'relative' }}
        >
          {imageContent3}
          {img3 && (
            <DefaultFlagButton
              is_default={defaultFlag === 3}
              onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン４A（p4a）】
  function renderPatternP4A() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4A, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4A, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4A, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);

    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4A, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item green w33 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item green w33 h100 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
        <div className="d-flex flex-column w33 h100">
          <div
            className={`start-mode-item yellow w100 h50 ${img3 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
            style={{ position: 'relative' }}
          >
            {imageContent3}
            {img3 && (
              <DefaultFlagButton
                is_default={defaultFlag === 3}
                onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
              />
            )}
          </div>
          <div
            className={`start-mode-item yellow w100 h50 ${img4 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
            style={{ position: 'relative' }}
          >
            {imageContent4}
            {img4 && (
              <DefaultFlagButton
                is_default={defaultFlag === 4}
                onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  //【パターン４B（p4b）】
  function renderPatternP4B() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4B, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4B, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4B, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);
    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P4B, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item orange w50 h50 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item orange w50 h50 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
        <div
          className={`start-mode-item orange w50 h50 ${img3 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
          style={{ position: 'relative' }}
        >
          {imageContent3}
          {img3 && (
            <DefaultFlagButton
              is_default={defaultFlag === 3}
              onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
            />
          )}
        </div>
        <div
          className={`start-mode-item orange w50 h50 ${img4 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
          style={{ position: 'relative' }}
        >
          {imageContent4}
          {img4 && (
            <DefaultFlagButton
              is_default={defaultFlag === 4}
              onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン５A（p5a）】
  function renderPatternP5A() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5A, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5A, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5A, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);
    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5A, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);
    //position 5
    const eatingWayTimeFrame5 = eatingWayTimeFrames?.find((item) => item.position === 5);
    const areaType5 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5A, 5);
    const img5 = getImageByLanguageAndAreaType(eatingWayTimeFrame5, areaType5);
    const imageContent5 = renderImageContent(img5, areaType5);
    const exceptEatingWayIds5: number[] = [];
    if (eatingWayTimeFrame5) exceptEatingWayIds5.push(eatingWayTimeFrame5.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item green w33 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div className="d-flex flex-column flex-wrap w66 h100">
          <div
            className={`start-mode-item yellow w50 h50 ${img2 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
            style={{ position: 'relative' }}
          >
            {imageContent2}
            {img2 && (
              <DefaultFlagButton
                is_default={defaultFlag === 2}
                onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
              />
            )}
          </div>
          <div
            className={`start-mode-item yellow w50 h50 ${img3 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
            style={{ position: 'relative' }}
          >
            {imageContent3}
            {img3 && (
              <DefaultFlagButton
                is_default={defaultFlag === 3}
                onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
              />
            )}
          </div>
          <div
            className={`start-mode-item yellow w50 h50 ${img4 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
            style={{ position: 'relative' }}
          >
            {imageContent4}
            {img4 && (
              <DefaultFlagButton
                is_default={defaultFlag === 4}
                onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
              />
            )}
          </div>
          <div
            className={`start-mode-item yellow w50 h50 ${img5 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 5, areaType5, exceptEatingWayIds5)}
            style={{ position: 'relative' }}
          >
            {imageContent5}
            {img5 && (
              <DefaultFlagButton
                is_default={defaultFlag === 5}
                onReflect={handleReflectDefaultFlagButton.bind(null, 5)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  //【パターン５B（p5b）】
  function renderPatternP5B() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5B, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5B, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5B, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);
    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5B, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);
    //position 5
    const eatingWayTimeFrame5 = eatingWayTimeFrames?.find((item) => item.position === 5);
    const areaType5 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P5B, 5);
    const img5 = getImageByLanguageAndAreaType(eatingWayTimeFrame5, areaType5);
    const imageContent5 = renderImageContent(img5, areaType5);
    const exceptEatingWayIds5: number[] = [];
    if (eatingWayTimeFrame5) exceptEatingWayIds5.push(eatingWayTimeFrame5.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item blue w50 h100 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div className="d-flex flex-column flex-wrap w50 h100">
          <div
            className={`start-mode-item light-violet w50 h50 ${img2 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
            style={{ position: 'relative' }}
          >
            {imageContent2}
            {img2 && (
              <DefaultFlagButton
                is_default={defaultFlag === 2}
                onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
              />
            )}
          </div>
          <div
            className={`start-mode-item light-violet w50 h50 ${img3 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
            style={{ position: 'relative' }}
          >
            {imageContent3}
            {img3 && (
              <DefaultFlagButton
                is_default={defaultFlag === 3}
                onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
              />
            )}
          </div>
          <div
            className={`start-mode-item light-violet w50 h50 ${img4 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
            style={{ position: 'relative' }}
          >
            {imageContent4}
            {img4 && (
              <DefaultFlagButton
                is_default={defaultFlag === 4}
                onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
              />
            )}
          </div>
          <div
            className={`start-mode-item light-violet w50 h50 ${img5 ? 'border-0' : ''}`}
            onClick={handleOpenSelectEatingWayModal.bind(null, 5, areaType5, exceptEatingWayIds5)}
            style={{ position: 'relative' }}
          >
            {imageContent5}
            {img5 && (
              <DefaultFlagButton
                is_default={defaultFlag === 5}
                onReflect={handleReflectDefaultFlagButton.bind(null, 5)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  //【パターン６A（p6a）】
  function renderPatternP6A() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);
    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);
    //position 5
    const eatingWayTimeFrame5 = eatingWayTimeFrames?.find((item) => item.position === 5);
    const areaType5 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 5);
    const img5 = getImageByLanguageAndAreaType(eatingWayTimeFrame5, areaType5);
    const imageContent5 = renderImageContent(img5, areaType5);
    const exceptEatingWayIds5: number[] = [];
    if (eatingWayTimeFrame5) exceptEatingWayIds5.push(eatingWayTimeFrame5.eating_way.id);
    //position 6
    const eatingWayTimeFrame6 = eatingWayTimeFrames?.find((item) => item.position === 6);
    const areaType6 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6A, 6);
    const img6 = getImageByLanguageAndAreaType(eatingWayTimeFrame6, areaType6);
    const imageContent6 = renderImageContent(img6, areaType6);
    const exceptEatingWayIds6: number[] = [];
    if (eatingWayTimeFrame6) exceptEatingWayIds6.push(eatingWayTimeFrame6.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item yellow w33 h50 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item yellow w33 h50 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
        <div
          className={`start-mode-item yellow w33 h50 ${img3 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
          style={{ position: 'relative' }}
        >
          {imageContent3}
          {img3 && (
            <DefaultFlagButton
              is_default={defaultFlag === 3}
              onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
            />
          )}
        </div>
        <div
          className={`start-mode-item yellow w33 h50 ${img4 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
          style={{ position: 'relative' }}
        >
          {imageContent4}
          {img4 && (
            <DefaultFlagButton
              is_default={defaultFlag === 4}
              onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
            />
          )}
        </div>
        <div
          className={`start-mode-item yellow w33 h50 ${img5 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 5, areaType5, exceptEatingWayIds5)}
          style={{ position: 'relative' }}
        >
          {imageContent5}
          {img5 && (
            <DefaultFlagButton
              is_default={defaultFlag === 5}
              onReflect={handleReflectDefaultFlagButton.bind(null, 5)}
            />
          )}
        </div>
        <div
          className={`start-mode-item yellow w33 h50 ${img6 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 6, areaType6, exceptEatingWayIds6)}
          style={{ position: 'relative' }}
        >
          {imageContent6}
          {img6 && (
            <DefaultFlagButton
              is_default={defaultFlag === 6}
              onReflect={handleReflectDefaultFlagButton.bind(null, 6)}
            />
          )}
        </div>
      </div>
    );
  }

  //【パターン６B（p6b）】
  function renderPatternP6B() {
    //position 1
    const eatingWayTimeFrame1 = eatingWayTimeFrames?.find((item) => item.position === 1);
    const areaType1 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 1);
    const img1 = getImageByLanguageAndAreaType(eatingWayTimeFrame1, areaType1);
    const imageContent1 = renderImageContent(img1, areaType1);
    const exceptEatingWayIds1: number[] = [];
    if (eatingWayTimeFrame1) exceptEatingWayIds1.push(eatingWayTimeFrame1.eating_way.id);
    //position 2
    const eatingWayTimeFrame2 = eatingWayTimeFrames?.find((item) => item.position === 2);
    const areaType2 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 2);
    const img2 = getImageByLanguageAndAreaType(eatingWayTimeFrame2, areaType2);
    const imageContent2 = renderImageContent(img2, areaType2);
    const exceptEatingWayIds2: number[] = [];
    if (eatingWayTimeFrame2) exceptEatingWayIds2.push(eatingWayTimeFrame2.eating_way.id);
    //position 3
    const eatingWayTimeFrame3 = eatingWayTimeFrames?.find((item) => item.position === 3);
    const areaType3 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 3);
    const img3 = getImageByLanguageAndAreaType(eatingWayTimeFrame3, areaType3);
    const imageContent3 = renderImageContent(img3, areaType3);
    const exceptEatingWayIds3: number[] = [];
    if (eatingWayTimeFrame3) exceptEatingWayIds3.push(eatingWayTimeFrame3.eating_way.id);
    //position 4
    const eatingWayTimeFrame4 = eatingWayTimeFrames?.find((item) => item.position === 4);
    const areaType4 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 4);
    const img4 = getImageByLanguageAndAreaType(eatingWayTimeFrame4, areaType4);
    const imageContent4 = renderImageContent(img4, areaType4);
    const exceptEatingWayIds4: number[] = [];
    if (eatingWayTimeFrame4) exceptEatingWayIds4.push(eatingWayTimeFrame4.eating_way.id);
    //position 5
    const eatingWayTimeFrame5 = eatingWayTimeFrames?.find((item) => item.position === 5);
    const areaType5 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 5);
    const img5 = getImageByLanguageAndAreaType(eatingWayTimeFrame5, areaType5);
    const imageContent5 = renderImageContent(img5, areaType5);
    const exceptEatingWayIds5: number[] = [];
    if (eatingWayTimeFrame5) exceptEatingWayIds5.push(eatingWayTimeFrame5.eating_way.id);
    //position 6
    const eatingWayTimeFrame6 = eatingWayTimeFrames?.find((item) => item.position === 6);
    const areaType6 = getAreaTypeByPatternAndPosition(PATTERN_TYPE.P6B, 6);
    const img6 = getImageByLanguageAndAreaType(eatingWayTimeFrame6, areaType6);
    const imageContent6 = renderImageContent(img6, areaType6);
    const exceptEatingWayIds6: number[] = [];
    if (eatingWayTimeFrame6) exceptEatingWayIds6.push(eatingWayTimeFrame6.eating_way.id);

    return (
      <div className="start-mode-list">
        <div
          className={`start-mode-item orange w50 h50 ${img1 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 1, areaType1, exceptEatingWayIds1)}
          style={{ position: 'relative' }}
        >
          {imageContent1}
          {img1 && (
            <DefaultFlagButton
              is_default={defaultFlag === 1}
              onReflect={handleReflectDefaultFlagButton.bind(null, 1)}
            />
          )}
        </div>
        <div
          className={`start-mode-item orange w50 h50 ${img2 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 2, areaType2, exceptEatingWayIds2)}
          style={{ position: 'relative' }}
        >
          {imageContent2}
          {img2 && (
            <DefaultFlagButton
              is_default={defaultFlag === 2}
              onReflect={handleReflectDefaultFlagButton.bind(null, 2)}
            />
          )}
        </div>
        <div
          className={`start-mode-item light-violet w25 h50 ${img3 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 3, areaType3, exceptEatingWayIds3)}
          style={{ position: 'relative' }}
        >
          {imageContent3}
          {img3 && (
            <DefaultFlagButton
              is_default={defaultFlag === 3}
              onReflect={handleReflectDefaultFlagButton.bind(null, 3)}
            />
          )}
        </div>
        <div
          className={`start-mode-item light-violet w25 h50 ${img4 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 4, areaType4, exceptEatingWayIds4)}
          style={{ position: 'relative' }}
        >
          {imageContent4}
          {img4 && (
            <DefaultFlagButton
              is_default={defaultFlag === 4}
              onReflect={handleReflectDefaultFlagButton.bind(null, 4)}
            />
          )}
        </div>
        <div
          className={`start-mode-item light-violet w25 h50 ${img5 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 5, areaType5, exceptEatingWayIds5)}
          style={{ position: 'relative' }}
        >
          {imageContent5}
          {img5 && (
            <DefaultFlagButton
              is_default={defaultFlag === 5}
              onReflect={handleReflectDefaultFlagButton.bind(null, 5)}
            />
          )}
        </div>
        <div
          className={`start-mode-item light-violet w25 h50 ${img6 ? 'border-0' : ''}`}
          onClick={handleOpenSelectEatingWayModal.bind(null, 6, areaType6, exceptEatingWayIds6)}
          style={{ position: 'relative' }}
        >
          {imageContent6}
          {img6 && (
            <DefaultFlagButton
              is_default={defaultFlag === 6}
              onReflect={handleReflectDefaultFlagButton.bind(null, 6)}
            />
          )}
        </div>
      </div>
    );
  }

  function renderPattern(patternName: string) {
    let pageRender: JSX.Element = <></>;
    switch (patternName) {
      case PATTERN_TYPE.P1:
        pageRender = renderPatternP1();
        break;
      case PATTERN_TYPE.P2A:
        pageRender = renderPatternP2A();
        break;
      case PATTERN_TYPE.P2B:
        pageRender = renderPatternP2B();
        break;
      case PATTERN_TYPE.P3A:
        pageRender = renderPatternP3A();
        break;
      case PATTERN_TYPE.P3B:
        pageRender = renderPatternP3B();
        break;
      case PATTERN_TYPE.P4A:
        pageRender = renderPatternP4A();
        break;
      case PATTERN_TYPE.P4B:
        pageRender = renderPatternP4B();
        break;
      case PATTERN_TYPE.P5A:
        pageRender = renderPatternP5A();
        break;
      case PATTERN_TYPE.P5B:
        pageRender = renderPatternP5B();
        break;
      case PATTERN_TYPE.P6A:
        pageRender = renderPatternP6A();
        break;
      case PATTERN_TYPE.P6B:
        pageRender = renderPatternP6B();
        break;
      default:
        pageRender = renderPatternP1();
    }

    return pageRender;
  }

  // loading status
  let isLoading = true;
  if (eatingWayTimeFrames) {
    isLoading = false;
  }

  if (isLoading) return null;
  // end loading status

  const pageContent = renderPattern(selectedPattern);

  return (
    <>
      <Modal
        show
        size="lg"
        dialogClassName="eating-way-time-frame-modal"
        backdrop="static"
        onHide={handleClose}
        centered
      >
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}></Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <Row className="mb-2">
            <Col>
              <div className="mb-3">
                <FeatureTitleBar title={t('EatingWayTimeFrameModal.head_title')} />
              </div>
              <div
                className="text-center small"
                dangerouslySetInnerHTML={{ __html: t('EatingWayTimeFrameModal.note2') }}
              ></div>
            </Col>
            <Col>
              <div
                className="small text-center mb-4"
                style={{ width: '320px' }}
                dangerouslySetInnerHTML={{ __html: t('EatingWayTimeFrameModal.note1') }}
              ></div>
              <div className="text-end">
                <div className="d-inline-flex gap-3">
                  <Form.Label column className="fw-bold">
                    {t('EatingWayTimeFrameModal.language')}：
                  </Form.Label>
                  <Form.Select ref={languageElemRef} className="text-center" style={{ width: '140px' }}>
                    {Object.keys(LANGUAGES).map((languageKey) => (
                      <option key={languageKey} value={languageKey}>
                        {LANGUAGES[languageKey]}
                      </option>
                    ))}
                  </Form.Select>
                  <Button onClick={handleChangeLanguage}>{t('EatingWayTimeFrameModal.change_display')}</Button>
                </div>
              </div>
            </Col>
          </Row>
          <div className="mb-2">
            <div>
              <Form.Label column className="fw-bold py-1">
                {t('EatingWayTimeFrameModal.sale_type')}：{saleType.name}
              </Form.Label>
            </div>
            <div>
              <Form.Label column className="fw-bold py-1">
                {t('EatingWayTimeFrameModal.time_frame')}：{timeFrame.name}
              </Form.Label>
            </div>
          </div>
          <Row className="mb-3">
            <Col>
              <div className="d-inline-flex gap-3">
                <Form.Label className="col-form-label fw-bold">{t('EatingWayTimeFrameModal.num_area')}：</Form.Label>
                <Form.Select
                  ref={numAreaElemRef}
                  className="text-center"
                  style={{ width: '100px' }}
                  defaultValue={selectedNumArea}
                  onChange={handleNumAreaChanged}
                >
                  {Object.keys(NUM_AREA_TYPE).map((num_area) => (
                    <option key={num_area} value={NUM_AREA_TYPE[num_area]}>
                      {NUM_AREA_TYPE[num_area]}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col>
              <div className="d-inline-flex gap-3">
                <Form.Label className="col-form-label fw-bold">
                  {t('EatingWayTimeFrameModal.pattern_type')}：
                </Form.Label>
                <Form.Select
                  ref={patternElemRef}
                  className="text-center"
                  style={{ width: '100px' }}
                  defaultValue={selectedPattern}
                >
                  {Object.keys(displayPattern).map((pattern_type) => (
                    <option key={pattern_type} value={pattern_type}>
                      {pattern_type}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col className="d-flex gap-3 justify-content-end">
              <Button className="btn--dark-green" onClick={handleSwitchLayout}>
                {t('EatingWayTimeFrameModal.switch_layout')}
              </Button>
              <Button className="btn--dark-green" onClick={handleSubmit}>
                {t('EatingWayTimeFrameModal.save')}
              </Button>
            </Col>
          </Row>
          <div>{pageContent}</div>
        </Modal.Body>
      </Modal>

      {showSelectEatingWayModal && selectEatingWayModalData && (
        <SelectEatingWayModal
          saleTypeId={selectEatingWayModalData.saleTypeId}
          areaType={selectEatingWayModalData.areaType}
          position={selectEatingWayModalData.position}
          exceptEatingWayIds={selectEatingWayModalData.exceptEatingWayIds}
          timeFrameId={selectEatingWayModalData.timeFrameId}
          onClose={handleCloseSelectEatingWayModal}
          onReflect={handleReflectSelectEatingWayModal}
        />
      )}
    </>
  );
}

export default EatingWayTimeFrameModal;
